import React from "react";
import {
  Search, FileText, Lock, Film, ShieldCheck, Wallet, Download,
  CheckCircle2, MessageSquare, RotateCcw, ArrowRight, Clock,
  Shield, Users, Mic,
} from "lucide-react";

const STEPS = [
  {
    n: 1,
    title: "CHOOSE A CREATOR",
    desc: "Browse creators, check their portfolio, reviews and prices.",
    Visual: VisualPickCreator,
    delay: "0ms",
  },
  {
    n: 2,
    title: "PLACE AN ORDER",
    desc: "You select the service, set the details and place your order.",
    Visual: VisualPlaceOrder,
    delay: "120ms",
  },
  {
    n: 3,
    title: "PAYMENT IN ESCROW",
    desc: "Your payment is securely held in escrow. The creator can see it, but can't access it yet.",
    Visual: VisualEscrow,
    delay: "240ms",
  },
  {
    n: 4,
    title: "CREATOR DELIVERS",
    desc: "The creator delivers the content (watermarked preview) through the platform.",
    Visual: VisualDelivers,
    delay: "360ms",
  },
  {
    n: 5,
    title: "YOU REVIEW & APPROVE",
    desc: "You review the content and approve it if it meets your expectations.",
    Visual: VisualReview,
    delay: "480ms",
  },
  {
    n: 6,
    title: "RELEASE & RECEIVE",
    desc: "Once approved, the creator is paid and you receive the final file (clean version).",
    Visual: VisualRelease,
    delay: "600ms",
  },
];

const PROTECTION = [
  { icon: ShieldCheck, title: "ESCROW PROTECTION", text: "Your payment is held safely until you approve the delivery." },
  { icon: Clock,       title: "ON-TIME DELIVERY",  text: "Creators must deliver on time or you get a full refund." },
  { icon: Lock,        title: "SECURE FILES",      text: "Watermarked previews to prevent theft and leaks." },
  { icon: Users,       title: "DIRECT COMMUNICATION", text: "Chat directly with creators through our secure system." },
];

export default function HowItWorks() {
  return (
    <section
      data-testid="how-it-works"
      id="how-it-works"
      className="max-w-7xl mx-auto px-6 py-24"
    >
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto">
        <div
          className="inline-flex items-center gap-2 cc-overline text-[#00ff88]"
          style={{ letterSpacing: "0.3em" }}
        >
          <span>◈</span> HOW IT WORKS
        </div>
        <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]">
          <span className="text-white">SIMPLE.</span>{" "}
          <span className="text-[#00ff88]">SECURE.</span>{" "}
          <span className="text-white">RELIABLE.</span>
        </h2>
        <p className="mt-5 text-sm md:text-base text-[#a0a0b0] leading-relaxed">
          Cheatcraft protects both clients and creators at every step.
        </p>
      </div>

      {/* 6-step flow */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
        {STEPS.map((s) => (
          <StepCard key={s.n} step={s} />
        ))}
      </div>

      {/* Refund banner */}
      <div
        data-testid="hiw-refund-banner"
        className="mt-10 cc-surface rounded-2xl px-6 py-5 flex items-center gap-4 border border-[#00ff88]/25 cc-fade"
        style={{ animationDelay: "780ms" }}
      >
        <span className="w-10 h-10 rounded-xl bg-[#00ff88]/10 border border-[#00ff88]/30 flex items-center justify-center shrink-0">
          <Shield size={18} className="text-[#00ff88]" />
        </span>
        <div className="text-sm md:text-base text-white">
          <span className="cc-overline text-[#a0a0b0] mr-2">SLA</span>
          IF THE DEADLINE IS EXCEEDED, YOU GET A{" "}
          <span className="text-[#00ff88] font-semibold">FULL REFUND</span> —
          AUTOMATICALLY.
        </div>
      </div>

      {/* Trust pills */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        {PROTECTION.map((p, i) => (
          <ProtectionPill key={p.title} item={p} delay={`${860 + i * 90}ms`} index={i} />
        ))}
      </div>
    </section>
  );
}

const StepCard = ({ step }) => {
  const { n, title, desc, Visual, delay } = step;
  return (
    <div
      data-testid={`hiw-step-${n}`}
      className="cc-surface rounded-2xl p-5 md:p-6 border border-[#15152a] relative overflow-hidden cc-fade cc-card-hover"
      style={{ animationDelay: delay }}
    >
      <div className="absolute inset-0 cc-grid-bg opacity-[0.05] pointer-events-none" />
      <div className="relative z-10 flex items-start gap-3">
        <span className="w-9 h-9 rounded-full border-2 border-[#00ff88]/40 text-[#00ff88] font-mono text-sm flex items-center justify-center bg-[#00ff88]/5 shrink-0">
          {n}
        </span>
        <div className="flex-1 min-w-0">
          <div
            className="text-[11px] md:text-xs text-[#00ff88] font-semibold tracking-[0.18em]"
          >
            {title}
          </div>
          <p className="text-xs md:text-[13px] text-[#a0a0b0] leading-relaxed mt-1.5">
            {desc}
          </p>
        </div>
      </div>
      <div className="mt-5 relative z-10">
        <Visual />
      </div>
    </div>
  );
};

const ProtectionPill = ({ item, delay, index }) => {
  const { icon: Icon, title, text } = item;
  return (
    <div
      data-testid={`hiw-protection-pill-${index}`}
      className="cc-surface rounded-xl p-4 flex items-start gap-3 border border-[#15152a] cc-fade"
      style={{ animationDelay: delay }}
    >
      <span className="w-9 h-9 rounded-lg bg-[#00ff88]/8 border border-[#00ff88]/25 text-[#00ff88] flex items-center justify-center shrink-0">
        <Icon size={15} />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] tracking-[0.16em] text-[#00ff88] font-semibold">{title}</div>
        <div className="text-[11px] text-[#a0a0b0] mt-1 leading-snug">{text}</div>
      </div>
    </div>
  );
};

// ─── Step 1 — Pick a creator ────────────────────────────────────────
function VisualPickCreator() {
  return (
    <div className="rounded-xl border border-[#15152a] bg-[#0a0a14] p-3.5">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00ff88]/30 to-[#0a0a14] border border-[#15152a] flex items-center justify-center text-[#00ff88]">
            <Search size={14} />
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#00ff88] ring-2 ring-[#0a0a14] animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-white font-semibold">sqd</span>
            <span className="text-[10px] uppercase tracking-widest text-[#00ff88]">Online</span>
          </div>
          <div className="text-[10px] text-[#606070] font-mono mt-0.5">0 subs · 0 reviews · EN</div>
        </div>
        <div className="text-right">
          <div className="cc-overline">Price / video</div>
          <div className="font-mono text-base text-[#00ff88]" style={{ textShadow: "0 0 12px rgba(0,255,136,0.4)" }}>
            $2
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button className="text-[11px] border border-[#15152a] rounded-md py-1.5 text-[#a0a0b0] inline-flex items-center justify-center gap-1">
          <MessageSquare size={11} /> Contact
        </button>
        <button className="text-[11px] cc-btn-primary rounded-md py-1.5 inline-flex items-center justify-center gap-1">
          Place Order <ArrowRight size={11} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 2 — Place an order ────────────────────────────────────────
function VisualPlaceOrder() {
  return (
    <div className="rounded-xl border border-[#15152a] bg-[#0a0a14] p-3.5 space-y-2.5 text-[11px]">
      <SummaryRow k="Content" v="Tutorial" />
      <SummaryRow k="Game" v="Rust" />
      <SummaryRow k="Deadline" v="Within 7 days" muted />
      <div className="border-t border-dashed border-[#15152a] my-1" />
      <SummaryRow k="Base" v="$2" muted />
      <SummaryRow k="Add-ons" v="$25" muted />
      <SummaryRow k="Platform fee (10%)" v="$1" muted />
      <div className="border-t border-[#15152a] my-1" />
      <div className="flex items-center justify-between">
        <span className="cc-overline">YOU PAY</span>
        <span className="font-mono text-lg text-[#00ff88]" style={{ textShadow: "0 0 14px rgba(0,255,136,0.4)" }}>
          $28
        </span>
      </div>
    </div>
  );
}

const SummaryRow = ({ k, v, muted }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#606070]">{k}</span>
    <span className={`font-mono ${muted ? "text-[#a0a0b0]" : "text-white"}`}>{v}</span>
  </div>
);

// ─── Step 3 — Escrow / LTC ─────────────────────────────────────────
function VisualEscrow() {
  return (
    <div className="rounded-xl border border-[#15152a] bg-[#0a0a14] p-3.5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#00ff88] mb-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" />
        Pay with Litecoin
      </div>
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-md bg-[#06060c] border border-[#15152a] grid grid-cols-6 grid-rows-6 gap-[1px] p-1.5 shrink-0">
          {Array.from({ length: 36 }).map((_, i) => (
            <span
              key={i}
              className={`${[0,1,2,5,7,9,11,12,14,15,17,18,21,22,23,25,28,30,33,34,35].includes(i) ? "bg-[#00ff88]" : "bg-transparent"} rounded-[1px]`}
            />
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <div className="cc-overline">Send</div>
          <div className="font-mono text-sm text-white truncate">0.17347209 LTC</div>
          <div className="text-[10px] text-[#606070] font-mono mt-0.5">≈ $28 · expires in 14:32</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 cc-elevated rounded-lg px-3 py-2 border border-[#ffaa00]/30">
        <Lock size={12} className="text-[#ffaa00]" />
        <span className="text-[11px] text-[#ffaa00]">Funds locked · awaiting delivery</span>
      </div>
    </div>
  );
}

// ─── Step 4 — Creator delivers ─────────────────────────────────────
function VisualDelivers() {
  return (
    <div className="rounded-xl border border-[#15152a] bg-[#0a0a14] p-3.5">
      <div className="relative aspect-[16/9] rounded-md overflow-hidden bg-gradient-to-br from-[#0a0a14] via-[#06060c] to-[#0a0a14] border border-[#15152a]">
        <div className="absolute top-2 left-2 flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#00ff88] bg-[#00ff88]/10 border border-[#00ff88]/30 rounded-full px-2 py-0.5">
          <CheckCircle2 size={10} /> Delivered
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#ffaa00] bg-[#ffaa00]/10 border border-[#ffaa00]/30 rounded-full px-2 py-0.5">
          <Lock size={10} /> Watermarked
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-[#00ff88]/15 border border-[#00ff88]/40 flex items-center justify-center text-[#00ff88]">
            <Film size={18} />
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-[#00ff88]/15 font-mono text-[10px] tracking-[0.4em] rotate-[-18deg]">
            CHEATCRAFT · CHEATCRAFT
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mt-2.5">
        <button className="text-[10px] cc-btn-primary rounded-md py-1.5">Approve</button>
        <button className="text-[10px] border border-[#ff8888]/30 text-[#ff8888] rounded-md py-1.5 inline-flex items-center justify-center gap-1">
          <RotateCcw size={10} /> Revise
        </button>
        <button className="text-[10px] border border-[#15152a] text-[#a0a0b0] rounded-md py-1.5 inline-flex items-center justify-center gap-1">
          <MessageSquare size={10} /> Chat
        </button>
      </div>
    </div>
  );
}

// ─── Step 5 — Review & Approve ─────────────────────────────────────
function VisualReview() {
  return (
    <div className="rounded-xl border border-[#15152a] bg-[#0a0a14] p-3.5 flex flex-col items-center text-center">
      <div className="relative">
        <span className="absolute inset-0 rounded-full bg-[#00ff88]/15 blur-xl" />
        <span className="relative w-14 h-14 rounded-full bg-[#00ff88]/15 border border-[#00ff88]/40 flex items-center justify-center text-[#00ff88]">
          <ShieldCheck size={26} className="drop-shadow-[0_0_8px_rgba(0,255,136,0.7)]" />
        </span>
      </div>
      <div className="mt-3 text-sm text-white font-semibold">Looks good?</div>
      <div className="text-[11px] text-[#a0a0b0] mt-0.5 leading-snug max-w-[24ch]">
        <span className="text-[#00ff88]">Approve & release</span> the payment to the creator.
      </div>
      <div className="mt-3 cc-btn-primary rounded-md px-4 py-1.5 text-[11px] inline-flex items-center gap-1.5">
        <CheckCircle2 size={11} /> Approve & Release
      </div>
    </div>
  );
}

// ─── Step 6 — Release & Receive ────────────────────────────────────
function VisualRelease() {
  return (
    <div className="grid grid-cols-2 gap-2.5">
      <ReleaseCard
        icon={<Wallet size={16} className="text-[#00ff88]" />}
        title="Payment released"
        sub="Creator receives instantly"
      />
      <ReleaseCard
        icon={<Download size={16} className="text-[#00ff88]" />}
        title="You receive"
        sub="Final file (clean)"
      />
    </div>
  );
}

const ReleaseCard = ({ icon, title, sub }) => (
  <div className="rounded-xl border border-[#15152a] bg-[#0a0a14] p-3.5 flex flex-col items-center text-center">
    <span className="w-10 h-10 rounded-lg bg-[#00ff88]/10 border border-[#00ff88]/30 flex items-center justify-center">
      {icon}
    </span>
    <div className="mt-2.5 text-[11px] tracking-[0.16em] text-[#00ff88] font-semibold">{title.toUpperCase()}</div>
    <div className="text-[10px] text-[#a0a0b0] mt-1 leading-snug">{sub}</div>
  </div>
);
